/**
 * Generic Duplicate Entity Component
 *
 * This component provides a reusable interface for duplicating any entity type
 * (properties, portfolios, commercials, lots, investors, etc.) with consistent
 * UI and behavior patterns.
 *
 * @author Housefolios
 * @created 2025-01-27
 * @modified 2025-08-12
 */

import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { enqueueSnackbar } from 'notistack'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Grid,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItem,
  MenuItem,
  Typography,
  FormControl,
  InputLabel,
  Select,
  TextField,
  Checkbox,
  FormControlLabel,
  Divider,
  ListItemButton,
  Box,
} from '@mui/material'
import BlockUi from 'react-block-ui'
import { PacmanLoader } from 'react-spinners'

// Centralized lodash imports
import {
  map,
  head,
  merge,
  cloneDeep,
  includes,
  filter,
  isEmpty,
} from '@/utils/lodash'

// Import common components
import CreatePortfolio from '@/housefolios-components/Portfolio/CreatePortfolio'

/**
 * Generic Duplicate Entity Component
 *
 * @param {Object} props - Component props
 * @param {string} props.entityType - Type of entity to duplicate (e.g., 'property', 'portfolio', 'commercial')
 * @param {string} props.entityId - ID of the entity to duplicate
 * @param {Object} props.entity - The entity object to duplicate
 * @param {Array} props.portfolios - Available portfolios to choose from
 * @param {string} props.selectedPortfolio - Currently selected portfolio
 * @param {Function} props.setSelected - Function to clear selection after duplication
 * @param {boolean} props.notifications - Whether to show as notification menu item
 * @param {boolean} props.rightClickMenu - Whether to show as right-click menu item
 * @param {boolean} props.sidebarMenu - Whether to show in sidebar menu
 * @param {boolean} props.underwriting - Whether this is for underwriting context
 * @param {string} props.buttonText - Custom button text
 * @param {Object} props.session - User session object
 * @param {Object} props.config - Entity configuration object
 * @param {Function} props.onSuccess - Callback function after successful duplication
 * @param {Function} props.onError - Callback function after failed duplication
 * @param {Object} props.customValidation - Custom validation rules
 * @param {boolean} props.overLimit - should trigger limit dialog
 * @param {Function} props.toggleLimit - toggles the limit dialog
 */
function DuplicateEntityComponent(props) {
  const {
    entityType,
    entityId,
    entity,
    portfolios,
    selectedPortfolio,
    onPortfolioChange,
    setSelected,
    notifications,
    rightClickMenu,
    sidebarMenu,
    underwriting,
    buttonText,
    session,
    config,
    onSubmit,
    onSuccess,
    onError,
    customValidation,
    overLimit,
    toggleLimit,
  } = props

  // State management
  const [openDuplicate, setOpenDuplicate] = useState(false)
  const [loader, setLoader] = useState(false)
  const [openPortfolio, setOpenPortfolio] = useState(false)
  // selectedPortfolio is now managed by wrapper components - no duplicate state

  const handleOpenPortfolio = () => {
    setOpenPortfolio(true)
  }
  const handleClosePortfolio = () => {
    setOpenPortfolio(false)
  }

  // Additional state for entity-specific fields
  const [additionalState, setAdditionalState] = useState(
    config?.additionalFields || {},
  )

  // Handlers
  const handleOpenDuplicate = () => {
    if (overLimit) {
      props.toggleLimit(entityType)
      return
    }
    setOpenDuplicate(true)
    setLoader(false)
    // Initialize additional state with entity data
    if (entity && !isEmpty(config?.additionalFields)) {
      const initialAdditionalState = {}
      Object.keys(config.additionalFields).forEach((key) => {
        if (entity[key] !== undefined) {
          initialAdditionalState[key] = entity[key]
        }
      })
      setAdditionalState(initialAdditionalState)
    }
  }

  const handleCloseDuplicate = () => {
    setOpenDuplicate(false)
    setAdditionalState(config?.additionalFields || {})
  }

  const handlePortfolioChange = (event) => {
    if (onPortfolioChange) {
      onPortfolioChange(event.target.value)
    }
  }

  const handleAdditionalFieldChange = (field, value) => {
    setAdditionalState((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  // Check if all required additional fields are filled
  const areRequiredFieldsFilled = () => {
    if (isEmpty(config?.additionalFields)) return true

    return Object.entries(config.additionalFields).every(
      ([field, fieldConfig]) => {
        const { required = false } = fieldConfig
        if (!required) return true

        const value = additionalState[field]
        // Check if the field has a meaningful value
        if (typeof value === 'string') return value.trim() !== ''
        if (typeof value === 'boolean') return true // Checkboxes are always valid
        if (Array.isArray(value)) return value.length > 0
        return value !== null && value !== undefined
      },
    )
  }

  // Validation
  const validateForm = () => {
    if (!selectedPortfolio) {
      enqueueSnackbar('Please select a portfolio', {
        variant: 'warning',
      })
      return false
    }

    // Check required additional fields
    if (!areRequiredFieldsFilled()) {
      enqueueSnackbar('Please fill in all required fields', {
        variant: 'warning',
      })
      return false
    }

    if (customValidation) {
      const validationResult = customValidation(additionalState)
      if (!validationResult.isValid) {
        enqueueSnackbar(validationResult.message, {
          variant: 'warning',
        })
        return false
      }
    }

    return true
  }

  // Submit handler - this can be overridden by the specific wrapper component
  const handleSubmit = () => {
    if (onSubmit) {
      // Pass the additional state data to the wrapper component
      onSubmit(additionalState)
      // Close the dialog after successful submission
      handleCloseDuplicate()
    } else {
      // This should be implemented by the specific wrapper component
      console.warn(
        'onSubmit not implemented - use specific wrapper component',
      )
    }
  }

  // Utility functions
  const capitalizeFirst = (str) =>
    str.charAt(0).toUpperCase() + str.slice(1)

  const getDefaultName = () => {
    if (entity?.name) {
      return `Copy - ${entity.name}`
    }
    return `Copy ${capitalizeFirst(entityType)}`
  }

  const getEntityIcon = () => {
    const iconMap = {
      property: { style: 'fas', icon: 'home' },
      portfolio: { style: 'far', icon: 'folder' },
      commercial: { style: 'far', icon: 'building' },
      lot: { style: 'fas', icon: 'map-marker' },
      investor: { style: 'far', icon: 'user' },
      default: { style: 'far', icon: 'copy' },
    }
    return iconMap[entityType] || iconMap.default
  }

  // Render additional fields
  const renderAdditionalFields = () => {
    if (isEmpty(config?.additionalFields)) return null

    return (
      <Grid size={12}>
        {needsPortfolioSelection && <Divider sx={{ my: '1rem' }} />}
        {/* <Typography variant="h6">
          Additional Settings
        </Typography> */}
        <Grid container spacing={2}>
          {Object.entries(config.additionalFields).map(
            ([field, fieldConfig]) => {
              const {
                type = 'text',
                label,
                options,
                required = false,
              } = fieldConfig

              switch (type) {
                case 'select':
                  return (
                    <Grid size={12} key={field}>
                      <FormControl fullWidth required={required}>
                        <InputLabel>{label}</InputLabel>
                        <Select
                          value={additionalState[field] || ''}
                          onChange={(e) =>
                            handleAdditionalFieldChange(
                              field,
                              e.target.value,
                            )
                          }
                          label={label}
                        >
                          {options?.map((option) => (
                            <MenuItem
                              key={option.value}
                              value={option.value}
                            >
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )

                case 'checkbox':
                  return (
                    <Grid size={12} key={field}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={additionalState[field] || false}
                            onChange={(e) =>
                              handleAdditionalFieldChange(
                                field,
                                e.target.checked,
                              )
                            }
                          />
                        }
                        label={label}
                      />
                    </Grid>
                  )

                default:
                  return (
                    <Grid size={12} key={field}>
                      <TextField
                        fullWidth
                        label={label}
                        type={type}
                        value={additionalState[field] || ''}
                        onChange={(e) =>
                          handleAdditionalFieldChange(
                            field,
                            e.target.value,
                          )
                        }
                        required={required}
                      />
                    </Grid>
                  )
              }
            },
          )}
        </Grid>
      </Grid>
    )
  }

  // Render menu items
  const renderMenuItems = () => {
    if (rightClickMenu) {
      return (
        <ListItem
          onClick={handleOpenDuplicate}

          sx={{ borderRadius: '0.29rem', cursor: 'pointer' }}
        >
          <Grid container direction="row" spacing={1}>
            <Grid data-hf-grid="keep">
              <FontAwesomeIcon icon={[getEntityIcon().style, getEntityIcon().icon]} sx={{ fontSize: "1.15rem !important" }} />
            </Grid>
            <Grid data-hf-grid="keep">
              <Typography>
                {buttonText ||
                  `Duplicate ${capitalizeFirst(entityType)}`}
              </Typography>
            </Grid>
          </Grid>
        </ListItem>
      )
    }

    if (notifications) {
      return (
        <MenuItem
          onClick={handleOpenDuplicate}
          sx={{ my: '0.25rem' }}
        >
          <FontAwesomeIcon icon={[getEntityIcon().style, getEntityIcon().icon]} style={{ marginRight: 10 }} sx={{ fontSize: "1.15rem !important" }} />
          <span>Duplicate {capitalizeFirst(entityType)}</span>
        </MenuItem>
      )
    }

    if (sidebarMenu) {
      return (
        <ListItemButton onClick={handleOpenDuplicate}>
          <FontAwesomeIcon icon={[getEntityIcon().style, getEntityIcon().icon]} style={{ marginRight: 10 }} sx={{ fontSize: "1.15rem !important" }} />
          <span>Duplicate {capitalizeFirst(entityType)}</span>
        </ListItemButton>
      )
    }

    if (underwriting) {
      return (
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenDuplicate}
          style={{ marginLeft: 15 }}
        >
          Underwriting
        </Button>
      )
    }

    return null
  }

  // Only show portfolio selection for entities that need it
  const needsPortfolioSelection = [
    'property',
    'properties', // Multiple properties also need portfolio selection
    'commercial',
    'lot',
    'investor', // Investors also need portfolio selection
  ].includes(entityType)

  return (
    <>
      {/* Menu Items */}
      {renderMenuItems()}

      {/* Main Dialog */}
      <Dialog
        open={openDuplicate}
        onClose={handleCloseDuplicate}
        maxWidth="sm"
        fullWidth={true}
      >
        <DialogTitle>
          Duplicate {capitalizeFirst(entityType)}
        </DialogTitle>

        <DialogContent sx={{ p: '1.5rem' }}>
          <BlockUi
            blocking={loader}
            loader={
              <PacmanLoader color={'var(--primary)'} loading={true} />
            }
            message={
              <Box sx={{ color: '#368cff' }}>
                Duplicating {capitalizeFirst(entityType)}
              </Box>
            }
          >
            <Grid container spacing={3} direction="column">
              {/* Portfolio Selection */}
              {needsPortfolioSelection && (
                <Grid size={12}>
                  <Typography variant="h6" sx={{ mb: '0.5rem' }}>
                    Choose Destination Portfolio
                  </Typography>
                  <FormControl fullWidth>
                    <InputLabel>Portfolio</InputLabel>
                    <Select
                      value={selectedPortfolio || ''}
                      onChange={handlePortfolioChange}
                      label="Portfolio"
                      open={openPortfolio}
                      onOpen={handleOpenPortfolio}
                      onClose={handleClosePortfolio}
                      MenuProps={{ keepMounted: true }}
                    >
                      {map(portfolios, (portfolio) => (
                        <MenuItem
                          key={portfolio._id}
                          value={portfolio._id}
                        >
                          {portfolio.name}
                        </MenuItem>
                      ))}
                      {session?.me?.activeOrg?.member !== 'RWN' && (
                        <CreatePortfolio
                          dropdown={true}
                          setNewPortfolio={(portfolioId) => {
                            if (onPortfolioChange) {
                              onPortfolioChange(portfolioId)
                            }
                          }}
                          closeMenu={handleClosePortfolio}
                        />
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Additional Fields */}
              {renderAdditionalFields()}
            </Grid>
          </BlockUi>
        </DialogContent>

        <DialogActions sx={{ p: '1.5rem' }}>
          <Button onClick={handleCloseDuplicate} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={
              (!selectedPortfolio && needsPortfolioSelection) ||
              !areRequiredFieldsFilled() ||
              loader
            }
          >
            Duplicate {capitalizeFirst(entityType)}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

// PropTypes
DuplicateEntityComponent.propTypes = {
  entityType: PropTypes.string.isRequired,
  entityId: PropTypes.string.isRequired,
  entity: PropTypes.object,
  portfolios: PropTypes.array.isRequired,
  selectedPortfolio: PropTypes.string,
  onPortfolioChange: PropTypes.func,
  setSelected: PropTypes.func,
  notifications: PropTypes.bool,
  rightClickMenu: PropTypes.bool,
  sidebarMenu: PropTypes.bool,
  underwriting: PropTypes.bool,
  buttonText: PropTypes.string,
  session: PropTypes.object.isRequired,
  config: PropTypes.object,
  onSubmit: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  customValidation: PropTypes.func,
  overLimit: PropTypes.bool,
  toggleLimit: PropTypes.func,
}

export default DuplicateEntityComponent
